/**
 * Error Handling Utilities
 *
 * Utility functions for parsing and handling API errors.
 */

import { AxiosError } from 'axios';
import type { APIError, APIErrorResponse, ValidationErrorResponse } from '../types/api.types';

// ============================================================================
// ERROR TYPE GUARDS
// ============================================================================

/**
 * Check if error is an Axios error
 */
export const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError).isAxiosError === true;
};

/**
 * Check if error is an API error response
 */
export const isAPIErrorResponse = (data: unknown): data is APIErrorResponse => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'success' in data &&
    data.success === false &&
    'error' in data
  );
};

/**
 * Check if error is a validation error response
 */
export const isValidationErrorResponse = (data: unknown): data is ValidationErrorResponse => {
  return (
    isAPIErrorResponse(data) &&
    'code' in data &&
    data.code === 'VALIDATION_ERROR' &&
    'details' in data &&
    Array.isArray(data.details)
  );
};

// ============================================================================
// ERROR MESSAGE EXTRACTION
// ============================================================================

/**
 * Get error message from any error type
 * @param error - Error object (can be Error, AxiosError, or unknown)
 * @param fallback - Fallback message if error message cannot be extracted
 */
export const getErrorMessage = (error: unknown, fallback: string = 'An unexpected error occurred'): string => {
  // Handle Axios errors
  if (isAxiosError(error)) {
    return parseApiError(error);
  }

  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle object with message property
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  // Return fallback
  return fallback;
};

/**
 * Parse API error from Axios error
 * @param error - Axios error
 */
export const parseApiError = (error: AxiosError<APIErrorResponse>): string => {
  // Network error (no response)
  if (!error.response) {
    return 'Network error: Unable to connect to the server. Please check your internet connection.';
  }

  // API error response
  if (error.response.data && isAPIErrorResponse(error.response.data)) {
    const apiError = error.response.data.error;
    // Handle both string errors (legacy) and object errors (new format)
    if (typeof apiError === 'string') {
      return apiError;
    }
    if (typeof apiError === 'object' && apiError !== null && 'message' in apiError) {
      return (apiError as { message: string }).message;
    }
  }

  // HTTP status errors
  switch (error.response.status) {
    case 400:
      return 'Bad request: Please check your input and try again.';
    case 401:
      return 'Unauthorized: Please log in to continue.';
    case 403:
      return 'Forbidden: You do not have permission to perform this action.';
    case 404:
      return 'Not found: The requested resource could not be found.';
    case 409:
      return 'Conflict: This resource already exists or is in use.';
    case 422:
      return 'Validation error: Please check your input and try again.';
    case 429:
      return 'Too many requests: Please wait a moment and try again.';
    case 500:
      return 'Server error: Something went wrong on our end. Please try again later.';
    case 502:
      return 'Bad gateway: The server is temporarily unavailable.';
    case 503:
      return 'Service unavailable: The server is temporarily down for maintenance.';
    case 504:
      return 'Gateway timeout: The server took too long to respond.';
    default:
      return `An error occurred (${error.response.status}). Please try again.`;
  }
};

/**
 * Extract validation errors from API error response
 * @param error - Axios error
 */
export const parseValidationErrors = (error: AxiosError<ValidationErrorResponse>): Record<string, string> => {
  if (!error.response?.data || !isValidationErrorResponse(error.response.data)) {
    return {};
  }

  const errors: Record<string, string> = {};
  error.response.data.details.forEach((detail: { field: string; message: string }) => {
    errors[detail.field] = detail.message;
  });

  return errors;
};

// ============================================================================
// ERROR FORMATTING
// ============================================================================

/**
 * Format error for logging
 * @param error - Error object
 */
export const formatErrorForLogging = (error: unknown): {
  message: string;
  stack?: string;
  response?: unknown;
  status?: number;
} => {
  if (isAxiosError(error)) {
    return {
      message: getErrorMessage(error),
      status: error.response?.status,
      response: error.response?.data,
      stack: error.stack,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    message: String(error),
  };
};

/**
 * Convert error to APIError type
 * @param error - Error object
 */
export const toAPIError = (error: unknown): APIError => {
  if (isAxiosError(error)) {
    return {
      message: parseApiError(error),
      code: error.code,
      statusCode: error.response?.status,
      details: error.response?.data,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }

  return {
    message: String(error),
  };
};

// ============================================================================
// ERROR HANDLING HELPERS
// ============================================================================

/**
 * Create a user-friendly error message from validation errors
 * @param errors - Validation errors object
 */
export const formatValidationErrors = (errors: Record<string, string>): string => {
  const messages = Object.entries(errors).map(([field, message]) => `${field}: ${message}`);
  return messages.join(', ');
};

/**
 * Handle API error with optional callback
 * @param error - Error object
 * @param onError - Optional error callback
 */
export const handleApiError = (error: unknown, onError?: (error: APIError) => void): void => {
  const apiError = toAPIError(error);

  // Log error in development
  if (import.meta.env.DEV) {
    console.error('[API Error]', formatErrorForLogging(error));
  }

  // Call error callback if provided
  if (onError) {
    onError(apiError);
  }
};

/**
 * Check if error is a specific HTTP status code
 * @param error - Error object
 * @param status - HTTP status code
 */
export const isErrorStatus = (error: unknown, status: number): boolean => {
  return isAxiosError(error) && error.response?.status === status;
};

/**
 * Check if error is a 401 Unauthorized error
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  return isErrorStatus(error, 401);
};

/**
 * Check if error is a 403 Forbidden error
 */
export const isForbiddenError = (error: unknown): boolean => {
  return isErrorStatus(error, 403);
};

/**
 * Check if error is a 404 Not Found error
 */
export const isNotFoundError = (error: unknown): boolean => {
  return isErrorStatus(error, 404);
};

/**
 * Check if error is a network error (no response)
 */
export const isNetworkError = (error: unknown): boolean => {
  return isAxiosError(error) && !error.response;
};
